import type { FormInstance } from 'element-plus'
import { ref, type Ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'
import * as Feedback from '@/utils/feedback/mixed'

export function getRouter() {
  return useRouter()
}

export function getRoute() {
  return useRoute()
}

export function getUserStore() {
  return useUserStore()
}

export const getFormRef = (): Ref<FormInstance | undefined> => {
  return ref<FormInstance>()
}

/**
 * 获取格式化后的时间
 */
export function getFormatterTime() {
  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = String(currentDate.getMonth() + 1).padStart(2, '0')
  const day = String(currentDate.getDate()).padStart(2, '0')
  const hours = String(currentDate.getHours()).padStart(2, '0')
  const minutes = String(currentDate.getMinutes()).padStart(2, '0')
  const seconds = String(currentDate.getSeconds()).padStart(2, '0')
  const currentTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  return currentTime
}
/**
 * 获取表单默认参数
 * @param formItems
 */
export const getFormDefaultParams = (formItems: FormItem[]) => {
  return formItems.map((m) => ({ [m.labelKey]: m.labelValue })).reduce((prev, cur) => Object.assign(prev, cur), {})
}

/**
 * 获取表单 JSON 参数，并支持嵌套属性（如 ext.disposalMethod）
 */
export const parseFormJson = (form: { [key: string]: any }) => {
  const result: { [key: string]: any } = {}

  Object.keys(form).forEach((key) => {
    if (key.includes('.')) {
      setNestedProperty(result, key, form[key])
    } else {
      result[key] = form[key]
    }
  })

  return result
}

/**
 * 设置嵌套属性的值
 * @param obj 目标对象
 * @param path 属性路径（例如 'ext.disposalMethod'）
 * @param value 要设置的值
 */
function setNestedProperty(obj: Record<string, any>, path: string, value: any) {
  const keys = path.split('.')
  let currentLevel = obj

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    if (!currentLevel[key]) {
      currentLevel[key] = {}
    }
    currentLevel = currentLevel[key]
  }

  // 设置最后一个键的值
  currentLevel[keys[keys.length - 1]] = value
}

interface SearchItem {
  labelName: string
  labelKey: string
  labelValue: any
  type: 'input' | 'select' | 'date-picker'
  ext?: any
  autoSearch?: boolean // 选择后是否自动搜索
}

export const getSearchItemsParams = (searchItems: SearchItem[]) => {
  return searchItems.map((m) => ({ [m.labelKey]: m.labelValue })).reduce((prev, cur) => Object.assign(prev, cur), {})
}

// 通用下载方法
export async function downloadBlob(response: any) {
  if (!response.headers['content-disposition']) {
    const text = await response.data.text()
    const textJSON = JSON.parse(text)
    console.log('textJSON', textJSON)
    if (textJSON.message) {
      Feedback.Notification.error(textJSON.message)
    }
    return
  }
  // 提取文件名
  const filename = decodeURIComponent(response.headers['content-disposition'].match(/filename=(.*)/)[1])
  console.log('filename', filename)

  // 将二进制流转为blob
  const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

  // 创建新的URL并指向File对象或者Blob对象的地址
  const blobURL = window.URL.createObjectURL(blob)
  // 创建a标签，用于跳转至下载链接
  const tempLink = document.createElement('a')
  tempLink.style.display = 'none'
  tempLink.href = blobURL
  tempLink.setAttribute('download', filename)
  // 兼容：某些浏览器不支持HTML5的download属性
  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank')
  }
  // 挂载a标签
  document.body.appendChild(tempLink)
  tempLink.click()
  document.body.removeChild(tempLink)
  // 释放blob URL地址
  window.URL.revokeObjectURL(blobURL)
}

import { JSEncrypt } from 'jsencrypt'
export function rsaEncrypt(dataToEncrypt: string) {
  const encrypt = new JSEncrypt()
  const publicKey = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAokCazensl0k1eJ8Fdzvw
j3XnMN6WMcx2Y0gqySztiq1r11G7L+c4qP9Fq6rzSfDYUBUqPKt96LOzKxj+sNUF
dCogG1rpE6+GbyYA4QH0lWYn1a1nvLbT1FCLrS7Q7OTrgg/MOnJ5EC5+xaDzrSAz
9xjP7q/qDIKL7SjoSDc7bo/Fzhhvw80cvv+4YumXH3JNAdu+VSYn/3YgwsGJSAOz
+hE6nBhgh7MhWv6RO+/JSaYlseatqGP6zSRHqK6fHnMMrLYIi5QHKETgx4nlmV6k
2lGGw0mhi3a90c2Z9dnCUq5aJakakrEGofB0zX0fKLiWmOEXN72P3ZtEF6r7wgG1
1wIDAQAB
-----END PUBLIC KEY-----`

  encrypt.setPublicKey(publicKey)

  const encryptedData = encrypt.encrypt(dataToEncrypt)
  return encryptedData
}

export function getThisWeek() {
  const now = new Date()
  const endOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
  // 获取今天是星期几，0表示周日，1表示周一，以此类推
  const day = now.getDay() - 1
  // 本周开始的日期
  const Week = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day)
  const startOfWeek = new Date(Week.getFullYear(), Week.getMonth(), Week.getDate(), 0, 0, 0, 0)
  // 返回今天的开始和结束日期字符串
  return {
    start: formatDate(startOfWeek),
    end: formatDate(endOfWeek)
  }
}

export function getThisToday() {
  const now = new Date()
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
  // 返回今天的开始和结束日期字符串
  return {
    start: formatDate(startOfDay),
    end: formatDate(endOfDay)
  }
}

export function getThisMonth() {
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59) // 修复这里
  return {
    start: formatDate(startOfMonth),
    end: formatDate(endOfMonth)
  }
}
// 格式化日期
export function formatDate(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  const currentTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  return currentTime
}

// 格式化日期
export function formatDateYMD(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const currentTime = `${year}-${month}-${day}`
  return currentTime
}

/**
 * 基于vite.config中插件pxtoviewport的配置，计算长度转换
 * @param px
 * @param designWidth 设计稿宽度
 */
export function pxToVw(px: number, designWidth = 1920) {
  if (px) {
    return ((px / designWidth) * 100).toFixed(5)
  }
  return '0'
}

// /**
//  * 将 rem 转换为基于设计稿的 vw（校准版）
//  * @param {string} remStr - rem 值
//  * @param {number} [designWidth=1920] - 设计稿宽度
//  * @returns {string} - 校准后的 vw 值
//  */
// export function remToVwCss(remStr: string, designWidth = 1920) {
//   if (!remStr.endsWith('rem')) return remStr
//   const remValue = parseFloat(remStr.replace('rem', ''))
//   // 获取 --font-size-body 的 vw 值（如 "1vw" → 解析为 1）
//   const rootFontSizeVw = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--font-size-body')) || 1 // 默认 1vw

//   // 计算实际像素（假设设计稿宽度 1920px）
//   const pxPerVw = designWidth / 100 // 1vw = 19.2px (1920/100)
//   const targetPx = remValue * rootFontSizeVw * pxPerVw

//   // 转换回 vw（基于当前视窗）
//   const calibratedVw = (targetPx / window.innerWidth) * 100
//   return `${calibratedVw}vw`
// }

/**
 * 基于vite.config中插件pxtoviewport的配置，计算长度转换
 * @param px
 */
export function pxToVwCss(px: number | string | undefined) {
  if (px === undefined) return undefined
  if (typeof px === 'string') {
    if (px.endsWith('px')) {
      return pxToVw(parseInt(px)) + 'vw'
    }
    if (px.endsWith('vw')) {
      return px
    }
    if (px.endsWith('%')) {
      return px
    }
  } else if (typeof px === 'number') {
    return pxToVw(px) + 'vw'
  }
  return px
}

// /**
//  * 基于vite.config中插件pxtoviewport的配置，计算长度转换
//  * @param px
//  */
// export function convertToPx(px: number | string | undefined) {
//   if (px === undefined) return undefined
//   if (typeof px === 'string') {
//     if (px.endsWith('px')) {
//       return pxToVw(parseInt(px)) + 'vw'
//     }
//     if (px.endsWith('vw')) {
//       return px
//     }
//     if (px.endsWith('%')) {
//       return px
//     }
//   } else if (typeof px === 'number') {
//     return pxToVw(px) + 'vw'
//   }
//   return '0px'
// }

/**
 * 适配样式对象-主要是单位转换
 * @param style
 */
export function adaptStyle(style: { [key: string]: any }) {
  for (let key in style) {
    if (style.hasOwnProperty(key) && typeof style[key] === 'string') {
      if (style[key].endsWith('px')) {
        style[key] = pxToVwCss(style[key])
      }
      // else if (style[key].endsWith('rem')) {
      //   style[key] = remToVwCss(style[key])
      // }
    }
  }
  // console.log('filterObjectUndefined(style)', filterObjectUndefined(style))

  return filterObjectUndefined(style)
}

/**
 * 过滤对象的undefined属性
 * @param style
 */
export function filterObjectUndefined(obj: any) {
  obj = obj || {}
  return Object.keys(obj)
    .filter((key) => obj[key] !== undefined)
    .reduce((result: { [key: string]: any }, key) => {
      result[key] = obj[key]
      return result
    }, {})
}

/**
 * 防抖函数
 * 
 * // 示例使用
const debouncedFunction = debounce(function(this: any, value: string) {
    console.log(`Called with "${value}"`);
}, 500);

// 使用示例
let counter = 0;
setInterval(() => {
    debouncedFunction(`Call ${++counter}`);
}, 100);
 */
export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeoutId: ReturnType<typeof setTimeout>

  return function (this: any, ...args: Parameters<T>): void {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), wait)
  } as T
}

/**
 * 对象深拷贝
 * 使用 JSON.parse(JSON.stringify(object)) 可能会导致丢失的数据类型和特性：
1、函数：函数不会被序列化，因此在反序列化后的新对象中将丢失所有的函数。
2、循环引用：如果对象中存在循环引用，JSON.stringify() 会抛出一个错误。
3、原型链：对象的原型链中的属性不会被序列化，因此在反序列化后的新对象中将丢失原型链中的属性。
4、Symbol 属性：对象中的 Symbol 类型的属性不会被序列化。
5、不可枚举的属性：对象中的不可枚举属性（即那些通过 Object.defineProperty() 设置为 enumerable: false 的属性）不会被序列化。
6、特殊类型的值：例如 Date、RegExp、Map、Set 等特殊类型的值会被转换为其字符串表示形式，或者丢失其特殊行为。
 */
export function deepCopy(obj: any) {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * 深度克隆，可以解决 deepCopy 中丢失方法和其他数据类型的问题
 */
import lodash from 'lodash'
export function deepClone(obj: any) {
  return lodash.cloneDeep(obj)
}

// 预览对应当前图片列表
export const getPrivewImages = (index: number, imgList: string[]) => {
  let tempImgList = [...imgList] //所有图片地址
  if (index == 0) return tempImgList
  // 调整图片顺序，把当前图片放在第一位
  let start = tempImgList.splice(index)
  let remain = tempImgList.splice(0, index)
  return start.concat(remain) //将当前图片调整成点击缩略图的那张图片
}
